import { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Badge from "@mui/material/Badge";
import Modal from "@mui/material/Modal";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import MDAlert from "components/MDAlert";

import CrudService from "services/cruds-service";
import CustomizeDataTable from "examples/Tables/CustomizeDataTable";
import Grid from "@mui/material/Grid";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useMaterialUIController } from "context";
import moment from "moment";
import GoogleMapComponent from "examples/Maps";
import MapIcon from "@mui/icons-material/Map";
import RefreshIcon from "@mui/icons-material/Refresh";
import PendingIcon from "@mui/icons-material/Pending";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import OpenInBrowserIcon from "@mui/icons-material/OpenInBrowser";
import JobForm from "../jobForm";
import MDModal from "components/MDModel";
import { JSONTree } from "react-json-tree";
import MDButton from "components/MDButton";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ColumnSetting from "../modal/column_setting";
import { AbilityContext } from "Can";
import { useAbility } from "@casl/react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CollateralLog from "../modal/collateral-log";
import { JSONTreeTheme } from "utils/constant";
import { COLUMN_SETTING } from "utils/constant";
import 'material-react-toastify/dist/ReactToastify.css';
import VirtualizedTable from "examples/Tables/VirtualizedTable";

function CampaignDashboard() {
  let { state } = useLocation();
  const ability = useAbility(AbilityContext);
  const [data, setData] = useState([]);
  // const [tableData, setTableData] = useState([])
  const [notification, setNotification] = useState({
    value: false,
    text: "",
  });
  const [total, setTotal] = useState(0);
  const [campaignName, setCampaignName] = useState("");
  const [totalCampaign, setTotalCampaign] = useState(0);
  const [totalDashboardView, setTotalDashboardView] = useState(0);
  const [totalEmailClick, setTotalEmailClick] = useState(0);
  const [totalEmailOpen, setTotalEmailOpen] = useState(0);
  const [selectedCampaign, setSelectedCampaign] = useState(
    localStorage.getItem("selectedCampaign") ?? ""
  );
  const [campaign, setCampaign] = useState([]);
  const [campaignTableData, setCampaignTableData] = useState([]);
  const [selectedTableData, setSelectedTableData] = useState([]);
  const [collateralModelOpen, setCollateralModelOpen] = useState(false);
  const [campaignModelOpen, setCampaignModelOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [gps, setGps] = useState({ latitude: "", longitude: "" });
  const [selectedCompanyRows, setSelectedCompanyRows] = useState([]);
  const [selectedCompanyData, setSelectedCompanyData] = useState([]);
  const [openGenerateModel, setOpenGenerateModal] = useState(false);
  const [openColumnSettingModel, setOpenColumnSettingModel] = useState(false);
  const [selectCompanyId, setSelectCompanyId] = useState(null);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [pageIndex, setPageIndex] = useState(
    localStorage.getItem("pageIndex") ?? 0
  );
  const [debouncePageIndex, setDebouncedPageIndex] = useState(
    localStorage.getItem("pageIndex") ?? 0
  );
  const [pageSize, setPageSize] = useState(
    localStorage.getItem("pageSize") ?? 100
  );
  const [apiSearch, setApiSearch] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState("");
  const [expand, setExpand] = useState(false);
  const [sorting, setSorting] = useState({
    orderBy: "",
    orderDirection: "asc",
  });
  const [columnSettings, setColumnSettings] = useState(COLUMN_SETTING);
  const [dynamicCollateralAttributes, setDynamicCollateralAttributes] =
    useState([]);
  const [advancedFilter, setAdvanceFilter] = useState([]);
  const [collateralLogData, setCollateralLogData] = useState([]);
  const [openCollateralLogEditModal, setOpenCollateralLogEditModal] =
    useState(false);
  const [selectedAll, setSelectedAll] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);

      let filters = JSON.stringify(advancedFilter.filters || [])

      // Fetch column settings and campaign dashboard data
      const [settingsResponse, dashboardResponse] = await Promise.all([
        CrudService.getSetting("CAMP_DASH"),
        CrudService.getCampaignDashboard({
          ...(filters.includes('campaign_id') ? {} : {campaign: selectedCampaign}),
          page: Number(pageIndex) + 1,
          pageSize,
          search: apiSearch,
          orderBy: sorting.orderBy,
          orderDirection: sorting.orderDirection,
          filters: JSON.stringify(advancedFilter.filters || []),
          operators: JSON.stringify(advancedFilter.operators || []),
        }),
      ]);

      // Merge and prioritize column settings
      const mergedColumnSettings = columnSettings.map((setting) => {
        const foundInApi = settingsResponse.columns.find(
          (apiSetting) => apiSetting.column_name === setting.column_name
        );
        return foundInApi || setting;
      });

      const remainingFromApi = settingsResponse.columns.filter(
        (apiSetting) =>
          !columnSettings.some(
            (setting) => setting.column_name === apiSetting.column_name
          )
      );

      const finalColumnSettings = [
        ...mergedColumnSettings,
        ...remainingFromApi,
      ].sort((a, b) => a.position - b.position);

      setColumnSettings(finalColumnSettings);

      // Process dynamic collateral attributes
      const allAttributes = dashboardResponse.campaignCollateral;

      const dynamicHideColumns = allAttributes.map((attribute) => ({
        column_name: attribute,
        column_type: "text",
        is_visible: false,
        position: finalColumnSettings.length + 1,
        backend_reference: attribute,
      }));

      const temp = dynamicHideColumns.filter(
        (att) =>
          !finalColumnSettings.some(
            (elem) => elem.column_name === att.column_name
          )
      );

      setDynamicCollateralAttributes(allAttributes);

      setColumnSettings((prevColumns) => [...prevColumns, ...temp]);

      if (dashboardResponse.data.length === 0 && pageIndex !== 0) {
        setPageIndex(0);
        setDebouncedPageIndex(0);
      }

      if (selectedAll) {
        setSelectedCompanyRows(
          dashboardResponse.data.map((elem) => elem.company_id)
        );
      }

      setData(dashboardResponse.data);

      if (filters.includes('campaign_id')) {
        let findCampaign = advancedFilter.filters.find((e) => e.field == 'campaign_id')
        if(Number(findCampaign?.value) !== Number(selectedCampaign)){
          setCampaignName("");
          // setSelectedCampaign(findCampaign.value)
          // const campaign_name = campaign.find(
          //   (e) => Number(e.campaign_id) === Number(findCampaign.value)
          // )?.name;
          // setCampaignName(campaign_name ?? "");
        }
      } else if (campaign.length) {
        const campaign_name = campaign.find(
          (e) => Number(e.campaign_id) === Number(selectedCampaign)
        )?.name;
        setCampaignName(campaign_name ?? "");
      }

      setIsLoading(false);

      const dashboardCountResponse =
        await CrudService.getCampaignDashboardCount({
          ...(filters.includes('campaign_id') ? {} : {campaign: selectedCampaign}),
          page: Number(pageIndex) + 1,
          pageSize,
          search: apiSearch,
          orderBy: sorting.orderBy,
          orderDirection: sorting.orderDirection,
          filters: JSON.stringify(advancedFilter.filters || []),
          operators: JSON.stringify(advancedFilter.operators || []),
        });

      setTotal(dashboardCountResponse.total);
      setTotalDashboardView(
        Number(dashboardCountResponse.CampaignCollateralEventsTotal)
      );
      setTotalEmailClick(
        Number(dashboardCountResponse.sendgrid_event_counts.click)
      );
      setTotalEmailOpen(
        Number(dashboardCountResponse.sendgrid_event_counts.open)
      );
    } catch (err) {
      console.error("Error fetching data:", err);
      setIsLoading(false);
    }
  }, [
    selectedCampaign,
    pageIndex,
    pageSize,
    apiSearch,
    sorting,
    advancedFilter,
  ]);

  useEffect(() => {
    setSelectedCompanyRows([])
  }, [pageIndex])

  useEffect(() => {
    async function fetchCampaign() {
      const campaignRes = await CrudService.getUniqueCampaigns();

      setCampaign(campaignRes.data);
      setTotalCampaign(campaignRes.data.length);
      const campaign_name = campaignRes.data.filter(
        (e) => e.campaign_id == selectedCampaign
      )[0]?.name;
      setCampaignName(campaign_name ?? "");
    }
    fetchCampaign();
  }, []);

  useEffect(() => {
    fetchData();
    document.title = `Rivio | Campaign Dashboard`;
    localStorage.setItem("selectedCampaign", selectedCampaign);
    localStorage.setItem("pageSize", pageSize);
    localStorage.setItem("pageIndex", Number(pageIndex));
  }, [fetchData]);

  useEffect(() => {
    setCampaignName("")

    setTotal(0);
    setTotalDashboardView(0);
    setTotalEmailClick(0);
    setTotalEmailOpen(0);
  }, [selectedCampaign])

  useEffect(() => {
    if (!state) return;
    setNotification({
      value: state.value,
      text: state.text,
    });
  }, [state]);

  useEffect(() => {
    setCampaignTableData(getCampaignRows(campaign));
  }, [campaign]);

  useEffect(() => {
    if (notification.value === true) {
      let timer = setTimeout(() => {
        setNotification({
          value: false,
          text: "",
        });
      }, 5000);
    }
  }, [notification]);

  useEffect(() => {
    let filterData = [...data];
    filterData = filterData.filter((e) =>
      selectedCompanyRows.includes(e.company_id)
    );
    setSelectedCompanyData(filterData);
  }, [selectedCompanyRows]);

  const hideShowColumn = useCallback(
    (name) => {
      const result = columnSettings?.some(
        (item) => item.column_name === name && item.is_visible
      );
      return result;
    },
    [columnSettings] // Ensure the dependency matches the correct state
  );

  const openModelView = (event, data) => {
    event.stopPropagation();
    let dataRows = getEventRows(data);
    let dataColumns = dataEventTableData;
    dataColumns.rows = dataRows;
    setSelectedTableData(dataColumns);
    setCollateralModelOpen(true);
  };
  const openContactsModelView = (event, data, company_id) => {
    event.stopPropagation();
    setSelectCompanyId(company_id);
    let dataRows = getContactsRows(data);
    let dataColumns = dataContactTableData;
    dataColumns.rows = dataRows;
    setSelectedTableData(dataColumns);
    setCollateralModelOpen(true);
  };

  const openCollateralModelView = (event, data) => {
    event.stopPropagation();
    let dataRows = getCollateralRows(data);
    let dataColumns = dataCollateralTableData;
    dataColumns.rows = dataRows;
    setSelectedTableData(dataColumns);
    setCollateralModelOpen(true);
  };

  const openMapModel = (event, value) => {
    event.stopPropagation();
    setGps(value);
    setOpenModal(true);
  };

  const openPage = (event, url) => {
    event.stopPropagation();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const getRows = (info) => {
    let updatedInfo = info.map((row) => {
      const collateralData =
        row?.CampaignCompany?.CampaignCollateralAttributeValuesObj || {};

      return {
        id: row?.company_id,
        company_id: row?.company_id,
        company_name: row?.CampaignCompany?.company_name,
        CampaignCollateralEventsCount:
          row?.CampaignCompany?.CampaignCollateralEventsCount || 0,
        company_phone: row?.CampaignCompany?.company_phone,
        company_email: row?.CampaignCompany?.company_email,
        company_url: row?.CampaignCompany?.company_url,
        primary_street_address1: row?.CampaignCompany?.primary_street_address1,
        primary_street_address2: row?.CampaignCompany?.primary_street_address2,
        primary_city: row?.CampaignCompany?.primary_city,
        primary_state: row?.CampaignCompany?.primary_state,
        primary_postal_code: row?.CampaignCompany?.primary_postal_code,
        company_category: row?.CampaignCompany?.company_category,
        latitude: row?.CampaignCompany?.latitude,
        longitude: row?.CampaignCompany?.longitude,
        CampaignEventCount: row.CampaignEventCount,
        collateral_count: row.CampaignCompany?.CampaignCollateralAttributeValues
          ? row.CampaignCompany?.CampaignCollateralAttributeValues.length
          : null,
        CampaignEvents: row.CampaignEvents,
        CampaignCompany: row.CampaignCompany,
        CampaignContact: row.CampaignCompany?.CampaignContacts,
        CampaignCollateralAttributeValues:
          row.CampaignCompany?.CampaignCollateralAttributeValues,
        LastInterest: row?.LastInterest,
        LastEventTimestamp: row?.LastEventTimestamp
          ? moment(row?.LastEventTimestamp).fromNow()
          : "",
        most_recent_notes: row?.recentNotes?.slice(0, 20) ?? "",
        content_text: "response idle",
        validation_status: "idle",
        fetch_status: "idle",
        update_status: "idle",
        validation_text: "validation idle",
        fetch_text: "fetch idle",
        update_text: "update idle",

        ...collateralData,
      };
    });
    return updatedInfo;
  };

  const tableData = useMemo(() => getRows(data), [data]);

  const dataTableData = useMemo(
    () => ({
      columns: [
        {
          Header: "Company ID",
          accessor: "company_id",
          ...(hideShowColumn("Company ID") ? {} : { show: false }),
          width: 150,
          notShrink: true,
        },
        {
          Header: "Dashboard",
          accessor: "dashboard",
          ...(hideShowColumn("Dashboard") ? {} : { show: false }),
          disableSortBy: true,
          width: 150,
          notShrink: true,
          Cell: (info) => {
            return info.row?.dashboard_url ? (
              <MDBox display="flex" alignItems="center">
                <IconButton
                  onClick={(e) => openPage(e, info.row?.dashboard_url)}
                >
                  <MDTypography>
                    <OpenInBrowserIcon />
                  </MDTypography>
                </IconButton>
              </MDBox>
            ) : (
              <></>
            );
          },
        },
        {
          Header: "dashboard view",
          accessor: "CampaignCollateralEventsCount",
          ...(hideShowColumn("dashboard view") ? {} : { show: false }),
          width: 150,
          notShrink: true,
        },
        {
          Header: "Company name",
          width: 250,
          notShrink: true,
          isCopy: true,
          accessor: "company_name",
          ...(hideShowColumn("Company name") ? {} : { show: false }),
        },
        {
          Header: "Company Phone",
          width: 150,
          notShrink: true,
          accessor: "company_phone",
          ...(hideShowColumn("Company Phone") ? {} : { show: false }),
        },
        {
          Header: "Company URL",
          width: 350,
          notShrink: true,
          isCopy: true,
          isRedirect: true,
          accessor: "company_url",
          ...(hideShowColumn("Company URL") ? {} : { show: false }),
        },
        {
          Header: "Company Email",
          width: 250,
          notShrink: true,
          accessor: "company_email",
          ...(hideShowColumn("Company Email") ? {} : { show: false }),
        },
        {
          Header: "address 1",
          width: 250,
          notShrink: true,
          accessor: "primary_street_address1",
          ...(hideShowColumn("address 1") ? {} : { show: false }),
        },
        {
          Header: "address 2",
          width: 150,
          notShrink: true,
          accessor: "primary_street_address2",
          ...(hideShowColumn("address 2") ? {} : { show: false }),
        },
        {
          Header: "city",
          width: 200,
          notShrink: true,
          accessor: "primary_city",
          ...(hideShowColumn("city") ? {} : { show: false }),
        },
        {
          Header: "state",
          width: 100,
          notShrink: true,
          accessor: "primary_state",
          ...(hideShowColumn("state") ? {} : { show: false }),
        },
        {
          Header: "postal code",
          width: 150,
          notShrink: true,
          accessor: "primary_postal_code",
          ...(hideShowColumn("postal code") ? {} : { show: false }),
        },
        {
          Header: "category",
          width: 200,
          notShrink: true,
          accessor: "company_category",
          ...(hideShowColumn("category") ? {} : { show: false }),
        },
        {
          Header: "Map",
          disableSortBy: true,
          ...(hideShowColumn("Map") ? {} : { show: false }),
          accessor: "map",
          width: 100,
          notShrink: true,
          disableSortBy: true,
          Cell: (info) => {
            return info.row.latitude && info.row.longitude ? (
              <MDBox display="flex" alignItems="center">
                <IconButton
                  onClick={(e) =>
                    openMapModel(e, {
                      latitude: info.row.latitude,
                      longitude: info.row.longitude,
                    })
                  }
                >
                  <MDTypography>
                    <MapIcon />
                  </MDTypography>
                </IconButton>
              </MDBox>
            ) : (
              <></>
            );
          },
        },
        {
          Header: "last event",
          width: 150,
          notShrink: true,
          accessor: "LastEventTimestamp",
          ...(hideShowColumn("last event") ? {} : { show: false }),
        },
        {
          Header: "event count",
          width: 150,
          notShrink: true,
          accessor: "CampaignEventCount",
          ...(hideShowColumn("event count") ? {} : { show: false }),
        },
        {
          Header: "interest",
          width: 100,
          notShrink: true,
          accessor: "LastInterest",
          ...(hideShowColumn("interest") ? {} : { show: false }),
          Cell: (info) => {
            return (
              info.value && (
                <Badge
                  color={
                    info.value.toLowerCase() == "yes" ? "success" : "error"
                  }
                  variant={"contained"}
                  badgeContent={info.value.toLowerCase()}
                  style={{
                    display: "inline-block",
                    whiteSpace: "nowrap",
                    marginLeft: "20px",
                  }}
                ></Badge>
              )
            );
          },
        },
        {
          Header: "Recent Notes",
          width: 300,
          notShrink: true,
          accessor: "most_recent_notes",
          ...(hideShowColumn("Recent Notes") ? {} : { show: false }),
        },
        {
          Header: "Contacts Logs",
          disableSortBy: true,
          ...(hideShowColumn("Contacts Logs") ? {} : { show: false }),
          accessor: "contacts_logs",
          width: 150,
          notShrink: true,
          disableSortBy: true,
          Cell: (info) => {
            return info.row?.CampaignContact?.length ? (
              <MDBox display="flex" alignItems="center">
                <IconButton
                  onClick={(e) =>
                    openContactsModelView(
                      e,
                      info.row?.CampaignContact,
                      info.row?.company_id
                    )
                  }
                >
                  <MDTypography>
                    <VisibilityIcon />
                  </MDTypography>
                </IconButton>
              </MDBox>
            ) : (
              <></>
            );
          },
        },
        {
          Header: "Event Logs",
          disableSortBy: true,
          ...(hideShowColumn("Event Logs") ? {} : { show: false }),
          accessor: "event_logs",
          width: 150,
          notShrink: true,
          disableSortBy: true,
          Cell: (info) => {
            return info.row.CampaignEventCount ? (
              <MDBox display="flex" alignItems="center">
                <IconButton
                  onClick={(e) => openModelView(e, info.row.CampaignEvents)}
                >
                  <MDTypography>
                    <VisibilityIcon />
                  </MDTypography>
                </IconButton>
              </MDBox>
            ) : (
              <></>
            );
          },
        },
        {
          Header: "Collateral Logs",
          disableSortBy: true,
          width: 150,
          notShrink: true,
          ...(hideShowColumn("Collateral Logs") ? {} : { show: false }),
          accessor: "collateral_logs",
          disableSortBy: true,
          Cell: (info) => {
            return info.row.collateral_count ? (
              <MDBox display="flex" alignItems="center">
                <IconButton
                  onClick={(e) =>
                    openCollateralModelView(
                      e,
                      info.row.CampaignCollateralAttributeValues
                    )
                  }
                >
                  <MDTypography>
                    <VisibilityIcon />
                  </MDTypography>
                </IconButton>
              </MDBox>
            ) : (
              <></>
            );
          },
        },
        ...dynamicCollateralAttributes.map((key) => {
          return {
            Header: key,
            accessor: key,
            width: 300,
            notShrink: true,
            disableSortBy: true,
            ...(hideShowColumn(key) ? {} : { show: false }),
          };
        }),
      ],

      rows: tableData, // Assuming 'info' is the array you pass to getRows
    }),
    [tableData, dynamicCollateralAttributes, columnSettings]
  );

  const getEventRows = (info) => {
    let updatedInfo = info.map((row) => {
      return {
        id: row.event_id,
        event_id: row.event_id,
        event_type: (
          <Badge
            color={"info"}
            variant={"contained"}
            badgeContent={row.event_type}
            style={{
              display: "inline-block",
              whiteSpace: "nowrap",
              marginLeft: "20px",
            }}
          ></Badge>
        ),
        contact_made: row.contact_made ? "Yes" : "No",
        event_notes: row.event_notes,
        date: moment(row.entry_timestamp).format("MM/DD/yyyy h:mm:ss A"),
      };
    });
    return updatedInfo;
  };

  const dataEventTableData = {
    columns: [
      { Header: "Event ID", accessor: "event_id" },
      { Header: "Event Type", accessor: "event_type" },
      { Header: "Contact Made", accessor: "contact_made" },
      { Header: "Event Notes", accessor: "event_notes" },
      { Header: "Date", accessor: "date" },
    ],

    rows: "",
  };

  const getContactsRows = (info) => {
    let updatedInfo = info.map((row) => {
      return {
        id: row.contact_id,
        contact_id: row.contact_id,
        contact_type: row.CampaignContactType.type_name,
        batch: row.CampaignContactsBatch.batch_name,
        first_name: row.first_name,
        last_name: row.last_name,
        email: row.email,
        phone: row.phone,
        department: row.department,
        linkedin: row.linkedin,
        position: row.position,
        event_type: (
          <Badge
            color={"info"}
            variant={"contained"}
            badgeContent={row.StatusType.status_name}
            style={{
              display: "inline-block",
              whiteSpace: "nowrap",
              marginLeft: "20px",
            }}
          ></Badge>
        ),
        // date: moment(row.entry_timestamp).format('MM/DD/yyyy h:mm:ss A'),
      };
    });
    return updatedInfo;
  };

  const dataContactTableData = {
    columns: [
      { Header: "Contact ID", accessor: "contact_id" },
      { Header: "Type", accessor: "contact_type" },
      { Header: "Batch", accessor: "batch" },
      { Header: "First Name", accessor: "first_name" },
      { Header: "Last Name", accessor: "last_name" },
      { Header: "Emai;", accessor: "email" },
      { Header: "phone", accessor: "phone" },
      { Header: "department", accessor: "department" },
      { Header: "linkedin", accessor: "linkedin" },
      { Header: "position", accessor: "position" },
      { Header: "Status", accessor: "event_type" },
    ],

    rows: "",
  };

  const getCollateralRows = (info) => {
    let updatedInfo = info.map((row) => {
      return {
        id: row.value_id,
        value_id: row.value_id,
        company_id: row.company_id,
        attribute_name: row.CampaignCollateralAttribute.attribute_name,
        attribute_value: row.value,
        date: moment(row.created_at).format("MM/DD/yyyy h:mm:ss A"),
      };
    });
    return updatedInfo;
  };

  const dataCollateralTableData = {
    columns: [
      { Header: "Value ID", accessor: "value_id" },
      { Header: "Attribute Name", accessor: "attribute_name" },
      { Header: "Attribute Value", accessor: "attribute_value" },
      { Header: "Date", accessor: "date" },
      {
        Header: "actions",
        disableSortBy: true,
        accessor: "",
        Cell: (info) => {
          return (
            <MDBox display="flex" alignItems="center">
              {ability.can("edit", "campaign") && (
                <Tooltip title="Edit Parameter">
                  <IconButton
                    onClick={(e) => clickEditHandler(info.cell.row.original)}
                  >
                    <MDTypography>
                      <EditIcon />
                    </MDTypography>
                  </IconButton>
                </Tooltip>
              )}
              {ability.can("delete", "campaign") && (
                <Tooltip title="Delete Parameter">
                  <IconButton
                    onClick={(e) =>
                      clickDeleteHandler(e, info.cell.row.original)
                    }
                  >
                    <MDTypography>
                      <DeleteIcon />
                    </MDTypography>
                  </IconButton>
                </Tooltip>
              )}
            </MDBox>
          );
        },
      },
    ],

    rows: "", // Assuming 'info' is the array you pass to getRows
  };

  const getCompanyRows = () => {
    let updatedInfo = selectedCompanyData.map((row) => {
      return {
        id: row.company_id,
        campaign_id: row.campaign_id,
        name: row.CampaignCompany.company_name,
        validation_status: row.validation_status,
        validation_text: row.validation_text,
        fetch_status: row.fetch_status,
        fetch_text: row.fetch_text,
        content_text: row.content_text,
        update_status: row.update_status,
        update_text: row.update_text,
      };
    });
    return updatedInfo;
  };

  const getCampaignRows = (info) => {
    let updatedInfo = info.map((row) => {
      let color = row?.status_type_id === 1 ? "success" : "error";
      let variant = "contained";
      let badgeContent = row?.StatusType?.status_name;

      return {
        type: "parameters",
        id: row.campaign_id,
        name: row.name,
        description: row.description,
        status: (
          <Badge
            color={color}
            variant={variant}
            badgeContent={badgeContent}
            style={{
              display: "inline-block",
              whiteSpace: "nowrap",
              marginLeft: "20px",
            }}
          ></Badge>
        ),
      };
    });
    return updatedInfo;
  };

  const dataCampaignTableData = {
    columns: [
      { Header: "ID", accessor: "id", width: "10%" },
      { Header: "name", accessor: "name", width: "30%" },
      { Header: "Description", accessor: "description", width: "40%" },
      { Header: "Status", accessor: "status", width: "20%" },
    ],

    rows: campaignTableData,
  };

  const openDialogContent = (event, value) => {
    event.stopPropagation();

    if (value && (value.content || value.sanitizeData)) {
      setDialogContent(value);
      setOpenDialog(true);
    }
  };

  const StatusRender = ({ status, value }) => {
    let render;
    switch (status) {
      case "idle":
        render = <PendingIcon />;
        break;

      case "running":
        render = <RefreshIcon className="refresh--icon" />;
        break;

      case "success":
        render = <CheckCircleIcon />;
        break;

      case "error":
        render = <ErrorIcon />;
        break;

      case "ignore":
        render = <RemoveCircleOutlineIcon />;
        break;

      case "valid":
        render = <CheckIcon />;
        break;

      case "invalid":
        render = <CloseIcon />;
        break;

      default:
        render = <PendingIcon />;
        break;
    }
    //
    return <Tooltip title={value}>{render}</Tooltip>;
  };

  const tableDeviceAllData = {
    columns: [
      {
        Header: "Status",
        accessor: "status",
        width: 300, notShrink: true,
        disableSortBy: true,
        Cell: (info) => {
          return (
            <MDBox sx={{ display: "flex", gap: "1rem" }}>
              <MDTypography variant="h6">
                <StatusRender
                  status={info.row.validation_status}
                  value={info.row.validation_text}
                />
              </MDTypography>
              <MDTypography variant="h6">
                <StatusRender
                  status={info.row.fetch_status}
                  value={info.row.fetch_text}
                />
              </MDTypography>
              <MDTypography variant="h6">
                {/* <StatusRender status={'idle'} value={info.row.content_text} /> */}
                <PendingIcon
                  onClick={(e) =>
                    openDialogContent(e, info.row.content_text)
                  }
                />
              </MDTypography>
              <MDTypography variant="h6">
                <StatusRender
                  status={info.row.update_status}
                  value={info.row.update_text}
                />
              </MDTypography>
            </MDBox>
          );
        },
      },
      { Header: "Company ID", accessor: "id", width: 200, notShrink: true },
      { Header: "Campaign Id", accessor: "campaign_id", width: 200, notShrink: true },
      { Header: "Company name", accessor: "name", width: 400, notShrink: true },
      // { Header: "phone", accessor: "phone", width: "15%" },
      // { Header: "email", accessor: "email", width: "20%" },
      // { Header: "website", accessor: "website", width: "20%" },
    ],

    rows: getCompanyRows(),
  };

  function updateStatus(type, status, text, index) {
    const updatedFilterData = [...selectedCompanyData];
    updatedFilterData[index][`${type}_status`] = status;
    updatedFilterData[index][`${type}_text`] = text;
    setSelectedCompanyData([...updatedFilterData]);
  }

  const onSaveContact = useCallback(() => {
    fetchData();
  }, [fetchData]);

  const updateDashboardData = (updatedRecords, index) => {
    const updatedData = [...data];
    updatedData[index].CampaignCompany = updatedRecords;
    setData(updatedData);
  };

  const handleSortChange = useCallback((sortBy, sortDirection) => {
    setSorting({ orderBy: sortBy, orderDirection: sortDirection });
  }, []);

  const reorderColumns = (columnsArray, showColumnArray) => {
    const columnMap = new Map(columnsArray.map((col) => [col.Header, col]));
    return showColumnArray
      .map((header) => columnMap.get(header))
      .filter(Boolean); // Ensure only valid columns are returned
  };

  const updatedColumns = useMemo(
    () => ({
      ...dataTableData,
      columns: reorderColumns(
        dataTableData.columns,
        columnSettings
          .filter((elem) => elem.is_visible) // Filter visible columns
          .map((elem) => elem.column_name) // Extract column names
      ),
    }),
    [dataTableData, columnSettings]
  );

  const clearFilter = useCallback(() => {
    setAdvanceFilter([]);
    setSelectedCompanyRows([]);
  }, []);

  const clickEditHandler = async (data) => {
    setCollateralLogData(data);
    setOpenCollateralLogEditModal(true);
  };

  const clickDeleteHandler = async (e, id) => {
    try {
      if (!confirm("Are you sure you want to delete this Collateral Log?")) {
        e.nativeEvent.stopImmediatePropagation();
      } else {
        const companyUUID = data.find(
          (comp) => comp?.company_id === id?.company_id
        ).uuid;

        await CrudService.deleteCollateralLog(id?.value_id);
        const res = await CrudService.getCampaignSingleDashboard(companyUUID);

        setData((prev) => {
          const index = prev.findIndex(
            (comp) => comp.company_id === id?.company_id
          );
          const matchingData = prev[index];

          if (index !== -1) {
            const updatedData = [...prev];
            updatedData[index] = { ...matchingData, ...res.data };
            return updatedData;
          }

          return prev;
        });

        // fetchData();
        setNotification({
          value: true,
          text: "The Collateral log has been successfully deleted",
        });
      }
    } catch (err) {
      if (err.hasOwnProperty("errors")) {
        setNotification({
          value: true,
          text: err.errors[0].detail,
        });
      }
      return null;
    }
    setCollateralModelOpen(false);
  };

  const handleCollateralLogSubmit = async (
    attributeValue,
    valueId,
    companyId
  ) => {
    try {
      const companyUUID = data.find(
        (comp) => comp?.company_id === companyId
      ).uuid;

      await CrudService.updateCollateralLog(valueId, {
        value: attributeValue.text,
      });
      const res = await CrudService.getCampaignSingleDashboard(companyUUID);

      setData((prev) => {
        const index = prev.findIndex((comp) => comp.company_id === companyId);
        const matchingData = prev[index];

        if (index !== -1) {
          const updatedData = [...prev];
          updatedData[index] = { ...matchingData, ...res.data };
          return updatedData;
        }

        return prev;
      });

      setOpenCollateralLogEditModal(false);
      setCollateralModelOpen(false);
    } catch (err) {
      console.error(err);
    }
  };

  const selecetdFunc = useCallback(
    () => setCampaignModelOpen(!campaignModelOpen),
    []
  );
  const generate = useCallback(
    () => setOpenGenerateModal(!openGenerateModel),
    []
  );
  const columnSetting = useCallback(
    () => setOpenColumnSettingModel(!openColumnSettingModel),
    []
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {notification.value && (
        <MDAlert color="info" my="20px">
          <MDTypography variant="body2" color="white">
            {notification.text || ""}
          </MDTypography>
        </MDAlert>
      )}
      <MDBox>
        <MDBox>
          <Modal
            open={openCollateralLogEditModal}
            onClose={() => setOpenCollateralLogEditModal(false)}
            aria-labelledby="modal-title"
            aria-describedby="modal-description"
          >
            <Card
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "40%", // Set the desired width here
                // backgroundColor: 'white',
                boxShadow: 24,
                p: 4,
              }}
            >
              <MDTypography>{collateralLogData.attribute_name}</MDTypography>
              <CollateralLog
                setOpen={setOpenCollateralLogEditModal}
                collateralLogData={collateralLogData}
                onSubmit={handleCollateralLogSubmit}
              />
            </Card>
          </Modal>
          <Modal
            open={collateralModelOpen}
            onClose={() => setCollateralModelOpen(!collateralModelOpen)}
            aria-labelledby="modal-title"
            aria-describedby="modal-description"
          >
            <Card
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "60%",
                backgroundColor: "white",
                p: 4,
                height: "50vh",
                overflow: "auto",
              }}
            >
              <DataTable table={selectedTableData} fixedWidth={true} />
            </Card>
          </Modal>
          <Modal
            open={campaignModelOpen}
            onClose={() => setCampaignModelOpen(!campaignModelOpen)}
            aria-labelledby="modal-title"
            aria-describedby="modal-description"
          >
            <Card
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "60%",
                backgroundColor: "white",
                p: 4,
                height: "60vh",
                overflow: "auto",
              }}
            >
              <DataTable
                table={dataCampaignTableData}
                fixedWidth={true}
                selectedRows={selectedCampaign.toString()}
                setSelectedRows={setSelectedCampaign}
                isSelection={true}
                canSearch={true}
                isMultiSelect={false}
                onRowRedirect={(row) => {
                  if (selectedCampaign === row) {
                    // Deselect the row if it's already selected
                    setSelectedCampaign("");
                    localStorage.removeItem("selectedCampaign");
                    setCampaignModelOpen(false);
                  } else {
                    // Select the new row
                    setSelectedCampaign(row);
                    localStorage.setItem("selectedCampaign", row);
                    setCampaignModelOpen(false);
                  }
                }}
              />
            </Card>
          </Modal>
          <Modal open={openModal} onClose={() => setOpenModal(false)}>
            <MDBox tabIndex={-1}>
              <GoogleMapComponent lat={gps.latitude} lng={gps.longitude} />
            </MDBox>
          </Modal>
          <Modal
            open={openGenerateModel}
            onClose={() => setOpenGenerateModal(false)}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.5)", // Backdrop effect
              }}
              onClick={() => setOpenGenerateModal(false)} // Close modal on backdrop click
            >
              <Card
                sx={{
                  width: "60%",
                  maxHeight: "80vh",
                  backgroundColor: "white",
                  p: 4,
                  overflow: "auto",
                  boxShadow: 24,
                  borderRadius: 2,
                }}
                onClick={(e) => e.stopPropagation()} // Prevent click inside the modal from closing it
              >
                <JobForm
                  selectedCompanyRows={selectedCompanyRows}
                  filterData={selectedCompanyData}
                  updateStatus={updateStatus}
                  updateDashboardData={updateDashboardData}
                  fetchData={fetchData}
                  type={"dashboard"}
                />
                <Box sx={{ flexGrow: 1, mt: 2 }}>
                  <VirtualizedTable
                    table={tableDeviceAllData}
                    tableHeight="calc(100vh - 320px)"
                  />
                </Box>
              </Card>
            </Box>
          </Modal>


          <ColumnSetting
            open={openColumnSettingModel}
            onClose={() => setOpenColumnSettingModel(false)}
            columns={columnSettings}
            setColumns={setColumnSettings}
          />

          <MDModal open={openDialog} onClose={() => setOpenDialog(false)}>
            <MDButton
              variant="gradient"
              color="dark"
              size="small"
              onClick={() => setExpand(!expand)}
            >
              {expand ? "Collapse All" : "Expand All"}
            </MDButton>
            <div className="jsontree_header">
              <Typography variant="h6"> Response </Typography>
              <Typography variant="h6"> Sanitize </Typography>
            </div>
            <div
              className="jsontree"
              style={{ backgroundColor: darkMode ? "#1a2035" : "#fff" }}
            >
              <JSONTree
                data={dialogContent?.content}
                theme={JSONTreeTheme}
                invertTheme={true}
                shouldExpandNodeInitially={() => expand}
                labelRenderer={([key]) => (
                  <span style={{ fontSize: "1rem" }}>{key}</span>
                )}
                valueRenderer={(raw) => (
                  <span style={{ fontSize: "1rem" }}>{raw}</span>
                )}
                hideRoot={!expand}
              />
              <JSONTree
                data={dialogContent?.sanitizeData}
                theme={JSONTreeTheme}
                invertTheme={true}
                shouldExpandNodeInitially={() => expand}
                labelRenderer={([key]) => (
                  <span style={{ fontSize: "1rem" }}>{key}</span>
                )}
                valueRenderer={(raw) => (
                  <span style={{ fontSize: "1rem" }}>{raw}</span>
                )}
                hideRoot={!expand}
              />
            </div>
          </MDModal>
          <Grid container spacing={2} sx={{ margin: 0, py: 2, gap: "10px" }}>
            {(selectedCampaign && campaignName) && (
              <Grid size={8}>
                <Card sx={{ p: 2, color: darkMode ? "#fff" : "#000" }}>
                  ID: {selectedCampaign}
                </Card>
              </Grid>
            )}
            {campaignName && (
              <Grid size={8}>
                <Card sx={{ p: 2, color: darkMode ? "#fff" : "#000" }}>
                  Name: {campaignName}
                </Card>
              </Grid>
            )}
            <Grid size={8}>
              <Card sx={{ p: 2, color: darkMode ? "#fff" : "#000" }}>
                Total Campaign: {totalCampaign}
              </Card>
            </Grid>
            <Grid size={8}>
              <Card sx={{ p: 2, color: darkMode ? "#fff" : "#000" }}>
                Total Companies: {total}
              </Card>
            </Grid>
            <Grid size={8}>
              <Card sx={{ p: 2, color: darkMode ? "#fff" : "#000" }}>
                Total Dashboard View: {totalDashboardView}
              </Card>
            </Grid>
            <Grid size={8}>
              <Card sx={{ p: 2, color: darkMode ? "#fff" : "#000" }}>
                Total Email click: {totalEmailClick}
              </Card>
            </Grid>
            <Grid size={8}>
              <Card sx={{ p: 2, color: darkMode ? "#fff" : "#000" }}>
                Total Email Open: {totalEmailOpen}
              </Card>
            </Grid>
          </Grid>
          <Card>
            <CustomizeDataTable
              table={updatedColumns}
              canSearch={true}
              canRefresh={true}
              searchFun={fetchData}
              entriesPerPage={{ defaultValue: pageSize }}
              selectData={campaign}
              selected={selectedCampaign}
              selecetdFunc={selecetdFunc}
              loader={isLoading}
              selectedRows={selectedCompanyRows}
              setSelectedRows={setSelectedCompanyRows}
              isSelection={true}
              generate={generate}
              columnSetting={columnSetting}
              debouncePageIndex={debouncePageIndex}
              setDebouncedPageIndex={setDebouncedPageIndex}
              pageIndex={pageIndex}
              setPageIndex={setPageIndex}
              setApiSearch={setApiSearch}
              pageSize={pageSize}
              setPageSize={setPageSize}
              total={total}
              company_id={selectCompanyId}
              onSave={onSaveContact}
              sorting={sorting}
              onSortChange={handleSortChange}
              clearAdvanceFilter={clearFilter}
              setAdvanceFilter={setAdvanceFilter}
              selectedAll={selectedAll}
              setSelectedAll={setSelectedAll}
            />
          </Card>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default CampaignDashboard;
